import React, { useState, useMemo, useEffect } from 'react';
import { X } from 'lucide-react';
import { DripEmailTemplate, getClientBranding } from '@/actions/drip-campaigns';

interface TemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: DripEmailTemplate;
}

const SAMPLE_FIELDS = [
  { key: 'lead.name',           label: 'Lead Name',     placeholder: 'John Doe' },
  { key: 'lead.email',          label: 'Lead Email',    placeholder: 'john@example.com' },
  { key: 'lead.phone',          label: 'Lead Phone',    placeholder: '+91 98765 43210' },
  { key: 'lead.status',         label: 'Lead Status',   placeholder: 'Open' },
  { key: 'client.company_name', label: 'Company Name',  placeholder: 'GrowthEye Demo' },
];

// Same logic as backend renderVars — strips HTML tags inside {{ }} then resolves the path
const renderVars = (text: string, data: Record<string, string>): string => {
  if (!text) return '';
  return text.replace(/\{\{([^}]+)\}\}/g, (_match, path: string) => {
    const cleanPath = path.replace(/<[^>]+>/g, '').trim();
    const keys = cleanPath.split('.');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let val: any = {};
    // Build nested object from flat keys map
    for (const [k, v] of Object.entries(data)) {
      const parts = k.split('.');
      if (parts.length === 2) {
        if (!val[parts[0]]) val[parts[0]] = {};
        val[parts[0]][parts[1]] = v;
      } else {
        val[k] = v;
      }
    }
    for (const key of keys) { val = val?.[key]; }
    return val !== undefined && val !== null ? String(val) : _match;
  });
};

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  isOpen,
  onClose,
  template,
}) => {
  const [sampleData, setSampleData] = useState<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};
    SAMPLE_FIELDS.forEach(f => { defaults[f.key] = f.placeholder; });
    return defaults;
  });
  const [activeTab, setActiveTab] = useState<'html' | 'plaintext'>('html');
  const [branding, setBranding] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      getClientBranding().then(res => {
        if (res.success && res.data) {
          setBranding(res.data);
          // Pre-fill company name from branding
          setSampleData(prev => ({
            ...prev,
            'client.company_name': res.data.company_name || prev['client.company_name']
          }));
        }
      }).catch(() => {});
    }
  }, [isOpen]);

  // Build the same branded wrapper as the backend getEmailWrapperHtml()
  const applyBrandingWrapper = (content: string): string => {
    if (!branding) return content;

    const logoHtml = branding.logo_url
      ? `<img src="${branding.logo_url}" alt="${branding.company_name || 'Logo'}" style="max-width:${branding.logo_width || 150}px; height:auto;" />`
      : '';

    const footerParts: string[] = [];
    if (branding.company_address) footerParts.push(`<p style="margin:5px 0;color:#666;font-size:12px;">${branding.company_address}</p>`);
    if (branding.support_email) footerParts.push(`<p style="margin:5px 0;color:#666;font-size:12px;">Support: <a href="mailto:${branding.support_email}" style="color:${branding.primary_color||'#0066cc'};">${branding.support_email}</a></p>`);
    if (branding.footer_text) footerParts.push(`<p style="margin:10px 0;color:#666;font-size:12px;">${branding.footer_text}</p>`);
    if (branding.show_unsubscribe_link !== false) footerParts.push(`<p style="margin:10px 0;color:#999;font-size:11px;"><a href="#" style="color:#999;text-decoration:underline;">${branding.unsubscribe_text || 'Unsubscribe from this list'}</a></p>`);

    const headerRow = logoHtml
      ? `<tr><td align="center" style="padding:30px 20px;background-color:${branding.primary_color||'#0066cc'};">${logoHtml}</td></tr>`
      : '';
    const footerRow = footerParts.length > 0
      ? `<tr><td align="center" style="padding:30px 20px;background-color:#f9f9f9;border-top:1px solid #e0e0e0;">${footerParts.join('')}</td></tr>`
      : '';

    return `<!DOCTYPE html><html><head><meta charset="UTF-8"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background-color:${branding.background_color||'#f4f4f4'};">
<table width="100%" cellpadding="0" cellspacing="0" style="background-color:${branding.background_color||'#f4f4f4'};">
  <tr><td align="center" style="padding:20px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background-color:#fff;border-radius:8px;overflow:hidden;">
      ${headerRow}
      <tr><td style="padding:40px 30px;color:${branding.secondary_color||'#333'};">${content}</td></tr>
      ${footerRow}
    </table>
  </td></tr>
</table>
</body></html>`;
  };

  const renderedSubject = useMemo(() => renderVars(template.subject_line || '', sampleData), [template, sampleData]);
  const rawHtml = useMemo(() => renderVars(template.html_body || '', sampleData), [template, sampleData]);
  const renderedHtml = useMemo(() => applyBrandingWrapper(rawHtml), [rawHtml, branding]);
  const renderedPlain = useMemo(() => renderVars(template.plain_text_body || '', sampleData), [template, sampleData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Template Preview</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{template.template_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Sidebar - Sample Data */}
          <div className="w-72 border-r border-gray-200 dark:border-gray-700 overflow-y-auto p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Sample Data</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Edit values to see how the email will look when sent.
            </p>
            <div className="space-y-4">
              {SAMPLE_FIELDS.map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {label}
                  </label>
                  <input
                    type="text"
                    value={sampleData[key] ?? ''}
                    onChange={(e) => setSampleData(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-brandColor"
                  />
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 font-mono">{`{{${key}}}`}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Main - Preview */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Subject */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Subject:</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{renderedSubject}</p>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
              <button
                onClick={() => setActiveTab('html')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'html'
                    ? 'border-brandColor text-brandColor'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                HTML Preview
              </button>
              <button
                onClick={() => setActiveTab('plaintext')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'plaintext'
                    ? 'border-brandColor text-brandColor'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Plain Text
              </button>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'html' ? (
                <div className="bg-white rounded-lg shadow-sm">
                  <iframe
                    srcDoc={renderedHtml}
                    className="w-full min-h-[600px] border border-gray-200 rounded-lg"
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg">
                  <pre className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white font-mono">
                    {renderedPlain || renderedHtml.replace(/<[^>]+>/g, '')}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Category:</span>{' '}
            <span className="capitalize">{template.category.replace('_', ' ')}</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
